import { MaterialResources } from "@/state/MaterialResources";
import React, { useMemo, useState } from "react";
import _ from "lodash";
import { MaterialSlot } from "@/3d/material/MaterialSlot";
import { ensureUniqueName } from "@/utils/string";
import { v4 as uuidv4 } from "uuid";

type MaterialSlotEditorProps = {
    materials: MaterialResources[];
    materialSlots: Record<string, MaterialSlot>;
    setMaterialSlots: (materialSlots: Record<string, MaterialSlot>) => void;
};

export const MaterialSlotEditor = ({
    materials,
    materialSlots,
    setMaterialSlots,
}: MaterialSlotEditorProps) => {
    const [selectedSlotId, setSelectedSlotId] = useState<string>();

    const selectedSlot = selectedSlotId !== undefined ? materialSlots[selectedSlotId] : undefined;

    const materialGroups = useMemo(() => {
        return _.uniq(materials.flatMap((material) => material.config.groups));
    }, [materials]);

    const addMaterialSlot = () => {
        const id = uuidv4();
        const newSlots = {
            ...materialSlots,
            [id]: {
                type: "fixed" as const,
                id,
                name: ensureUniqueName(
                    "New Material",
                    Object.values(materialSlots).map((slot) => slot.name)
                ),
                materialName: "",
            },
        };
        setMaterialSlots(newSlots);
        setSelectedSlotId(id);
    };

    const updateMaterialSlot = (slot: MaterialSlot) => {
        if (selectedSlotId === undefined) return;

        const newSlots = { ...materialSlots, [selectedSlotId]: slot };
        slot.name = ensureUniqueName(
            slot.name,
            Object.values(materialSlots)
                .filter((slot) => slot.id !== selectedSlotId)
                .map((slot) => slot.name)
        );
        setMaterialSlots(newSlots);
    };

    const changeMaterialSlotType = (type: string) => {
        if (type !== "fixed" && type !== "selectable") {
            return;
        }

        if (selectedSlot === undefined) return;
        updateMaterialSlot(
            type === "fixed"
                ? {
                      ...selectedSlot,
                      type: "fixed" as const,
                      materialName: "",
                  }
                : {
                      ...selectedSlot,
                      type: "selectable" as const,
                      materialNames: [],
                      groups: [],
                  }
        );
    };

    const deleteMaterialSlot = () => {
        if (selectedSlotId === undefined) return;

        const newSlots = { ...materialSlots };
        delete newSlots[selectedSlotId];
        setMaterialSlots(newSlots);
        setSelectedSlotId(undefined);
    };

    const toggleMaterialName = (materialName: string) => {
        if (!selectedSlot || selectedSlot.type !== "selectable") return;

        const materialNames = selectedSlot.materialNames.includes(materialName)
            ? selectedSlot.materialNames.filter((name) => name !== materialName)
            : [...selectedSlot.materialNames, materialName];

        updateMaterialSlot({
            ...selectedSlot,
            materialNames,
        });
    };

    const toggleGroup = (group: string) => {
        if (!selectedSlot || selectedSlot.type !== "selectable") return;

        const groups = selectedSlot.groups.includes(group)
            ? selectedSlot.groups.filter((g) => g !== group)
            : [...selectedSlot.groups, group];

        updateMaterialSlot({
            ...selectedSlot,
            groups,
        });
    };

    return (
        <div className="d-flex flex-column gap-2">
            <div>
                <b>Материал движка</b>
            </div>

            <select
                className="form-select form-select-sm"
                value={selectedSlotId ?? ""}
                onChange={(e) => setSelectedSlotId(e.target.value ? e.target.value : undefined)}
            >
                <option value="">Select material slot</option>
                {Object.values(materialSlots).map((slot) => (
                    <option key={slot.id} value={slot.id}>
                        {slot.name}
                    </option>
                ))}
            </select>
            <button className="btn btn-sm btn-primary" onClick={addMaterialSlot}>
                Add Material Slot
            </button>
            {selectedSlot && (
                <div className="d-flex flex-column gap-2 align-items-center">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={selectedSlot.name}
                        onChange={(e) =>
                            updateMaterialSlot({ ...selectedSlot, name: e.target.value })
                        }
                    />

                    <select
                        className="form-select form-select-sm"
                        value={selectedSlot.type}
                        onChange={(e) => changeMaterialSlotType(e.target.value)}
                    >
                        <option value="fixed">Fixed</option>
                        <option value="selectable">Selectable</option>
                    </select>

                    {selectedSlot.type === "fixed" && (
                        <select
                            className="form-select form-select-sm"
                            value={selectedSlot.materialName}
                            onChange={(e) =>
                                updateMaterialSlot({
                                    ...selectedSlot,
                                    materialName: e.target.value,
                                })
                            }
                        >
                            <option value="">Select material</option>
                            {materials.map(({ config: { name } }) => (
                                <option key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </select>
                    )}

                    {selectedSlot.type === "selectable" && (
                        <>
                            <div className="d-flex flex-column gap-1 w-100">
                                <b>Available Materials</b>
                                {materials.map(({ config: { name } }) => (
                                    <div key={name} className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`material-${name}`}
                                            checked={selectedSlot.materialNames.includes(name)}
                                            onChange={() => toggleMaterialName(name)}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`material-${name}`}
                                        >
                                            {name}
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <div className="d-flex flex-column gap-1 w-100">
                                <b>Material Groups</b>
                                {materialGroups.map((group) => (
                                    <div key={group} className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`group-${group}`}
                                            checked={selectedSlot.groups.includes(group)}
                                            onChange={() => toggleGroup(group)}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`group-${group}`}
                                        >
                                            {group}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <button className="btn btn-sm btn-danger" onClick={deleteMaterialSlot}>
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
};
